import {Routes} from '@angular/router';
import {Shell} from './core/layout/shell/shell';
import {onlyGuest} from './core/guards/only-guest.guard';
import {onlyAuth} from './core/guards/only-auth-guard';
import {HEADER_DATA} from '@core/layout/topbar/topbar.component';
import {IStatus} from '@core/enums/status.enum';
import {Company} from './shared/components/company-logo/company-logo';
import {createModuleGuard, createPermissionGuard} from '@core/auth/auth.guard';
import {hasModule, hasPermission, MODULES, PERMISSIONS} from '@core/auth/permissions';
import {inject} from '@angular/core';
import Keycloak from 'keycloak-js';

export const routes: Routes = [
  {
    path: 'verify/:token',
    loadComponent: () =>
      import('./features/verify/policy-verification/policy-verification').then(
        (c) => c.PolicyVerification,
      ),
  },
  {
    path: 'renewals/confirm/:token',
    loadComponent: () =>
      import('./features/comesa/renewal-confirmation/renewal-confirmation').then(
        (c) => c.RenewalConfirmationComponent,
      ),
  },
  {
    path: 'renewals/confirm',
    loadComponent: () =>
      import('./features/comesa/renewal-confirmation/renewal-confirmation').then(
        (c) => c.RenewalConfirmationComponent,
      ),
  },
  {
    path: '',
    canActivate: [onlyGuest],
    loadComponent: () => import('./features/login-page/login-page').then((c) => c.LoginPage),
  },
  {
    path: '',
    component: Shell,
    canActivate: [onlyAuth],
    children: [
      // {
      //   path: 'dashboard',
      //   loadComponent: () =>
      //     import('./features/dashboard/dashboard').then((m) => m.Dashboard),
      //   data: {
      //     [HEADER_DATA]: {
      //       title: 'Dashboard',
      //       subTitle: "All Providers",
      //       withDate: true,
      //       selector: 'providers'
      //     }
      //   }
      // },
      {
        path: 'comesa',
        canActivate: [createModuleGuard(MODULES.comesa, PERMISSIONS.dashboardView)],
        loadChildren: () => import('./features/comesa/comesa.routes').then((m) => m.COMESA_ROUTES),
        data: {
          [HEADER_DATA]: {
            title: 'YC Comesa',
            subTitle: 'COMESA Yellow Card · Cross-Border Vehicle Insurance',
            company: Company.COMESA,
            status: IStatus.Active,
          },
        },
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./features/comesa/reports/components/reports').then((m) => m.Reports),
        data: {
          [HEADER_DATA]: {
            title: 'Reports',
            subTitle: 'YC Comesa · Yellow Card Insurance',
            company: Company.COMESA,
            withBackButton: 'Back to YC Comesa',
            backLink: './comesa',
          },
        },
        children: [
          { path: '', redirectTo: 'premiums', pathMatch: 'full' },
          {
            path: 'premiums',
            loadComponent: () =>
              import('./features/comesa/reports/components/premiums/premiums').then(
                (m) => m.ReportsPremiums,
              ),
          },
          {
            path: 'cancellation',
            loadComponent: () =>
              import('./features/comesa/reports/components/cancellation').then(
                (m) => m.ReportsCancellation,
              ),
          },
          {
            path: 'reprints',
            loadComponent: () =>
              import('./features/comesa/reports/components/reprints').then(
                (m) => m.ReportsReprints,
              ),
          },
          {
            path: 'renewal',
            loadComponent: () =>
              import('./features/comesa/reports/components/renewal').then((m) => m.ReportsRenewal),
          },
          {
            path: 'certificates_in_use',
            loadComponent: () =>
              import('./features/comesa/reports/components/certificates').then(
                (m) => m.ReportsCertificates,
              ),
          },
          {
            path: 'client_report',
            loadComponent: () =>
              import('./features/comesa/reports/components/client-report').then(
                (m) => m.ReportsClientReport,
              ),
          },
        ],
      },
      {
        path: 'rawsur',
        canActivate: [
          createModuleGuard(MODULES.rawsur, PERMISSIONS.dashboardView, PERMISSIONS.borderView),
        ],
        loadChildren: () => import('./features/rawsur/rawsur.routes').then((m) => m.RAWSUR_ROUTES),
        data: {
          [HEADER_DATA]: {
            title: 'Rawsur',
            subTitle: 'Rawsur · Cross-Border Vehicle Insurance',
            company: Company.RAWSUR,
            status: IStatus.Active,
          },
        },
      },
      // {
      //   path: 'mubarak',
      //   loadChildren: () =>
      //     import('./features/comesa/comesa.routes').then((m) => m.COMESA_ROUTES),
      //   data: {
      //     [HEADER_DATA]: {
      //       title: 'Mubarak Drizit',
      //       subTitle: "UAE & Gulf Region · Multi-line Insurance",
      //       company: Company.MUBARAK,
      //       status: IStatus.Active
      //     }
      //   }
      // },
      {
        path: 'clients',
        canActivate: [createPermissionGuard(PERMISSIONS.clientsView)],
        loadComponent: () =>
          import('./features/rawsur/components/clients/clients').then((m) => m.Clients),
      },
      {
        path: 'subscriptions',
        canActivate: [createPermissionGuard(MODULES.rawsur, PERMISSIONS.platformAdmin)],
        loadComponent: () =>
          import('./features/subscription/subscription').then((m) => m.Subscription),
        data: {
          [HEADER_DATA]: {
            title: 'Subscriptions',
            subTitle: 'Daily digest & instant alert management'
          }
        }
      },
      {
        path: 'admin',
        loadChildren: () => import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
      },
      {
        path: 'users',
        canActivate: [createPermissionGuard(PERMISSIONS.usersView)],
        loadComponent: () =>
          import('./features/admin/users-list/users-list').then((m) => m.UsersList),
      },
      {
        path: 'users/:userId/edit',
        canActivate: [createPermissionGuard(PERMISSIONS.usersView)],
        loadComponent: () => import('./features/admin/user-edit/user-edit').then((m) => m.UserEdit),
      },
      {
        path: 'no-access',
        loadComponent: () => import('./features/no-access/no-access').then((m) => m.NoAccess),
      },
      {
        path: '**',
        redirectTo: () => {
          const keycloak = inject(Keycloak);
          // Module entitlement first: sending a rawsur-only user to /comesa lands them on a
          // page where every call 403s.
          if (
            hasModule(keycloak, MODULES.comesa) &&
            hasPermission(keycloak, PERMISSIONS.dashboardView)
          )
            return '/comesa';
          if (
            hasModule(keycloak, MODULES.rawsur) &&
            hasPermission(keycloak, PERMISSIONS.dashboardView, PERMISSIONS.borderView)
          )
            return '/rawsur';
          if (hasPermission(keycloak, PERMISSIONS.usersView)) return '/users';
          return '/no-access';
        },
      },
    ],
  },
];
